import { decodeJwtPayload, TAccessTokenClaims } from './jwt';
import { TAuthTokens, TRawTokenResponse } from './types';

const FALLBACK_LIFETIME_MS = 60_000;

const positiveNumber = (value: unknown): number | null =>
	typeof value === 'number' && value > 0 ? value : null;

const toEpochMs = (value: number): number => (value > 1e11 ? value : value * 1000);

const resolveExpiresAt = (
	response: TRawTokenResponse | null,
	claims: TAccessTokenClaims | null,
): number => {
	const fromClaims = positiveNumber(claims?.exp);
	if (fromClaims !== null) return fromClaims * 1000;

	const fromExpiresIn = positiveNumber(response?.expiresIn ?? response?.expires_in);
	if (fromExpiresIn !== null) return Date.now() + fromExpiresIn * 1000;

	const fromExpiresAt = positiveNumber(response?.expiresAt);
	if (fromExpiresAt !== null) return toEpochMs(fromExpiresAt);

	return Date.now() + FALLBACK_LIFETIME_MS;
};

export const normalizeTokenResponse = (response: TRawTokenResponse | null): TAuthTokens | null => {
	const accessToken = response?.accessToken ?? response?.access_token;
	if (!accessToken) return null;

	const claims = decodeJwtPayload<TAccessTokenClaims>(accessToken);

	return {
		accessToken,
		refreshToken: response?.refreshToken ?? response?.refresh_token ?? null,
		expiresAt: resolveExpiresAt(response, claims),
		sessionState:
			response?.sessionState ??
			response?.session_state ??
			claims?.session_state ??
			claims?.sid ??
			null,
	};
};
